import React, { useState } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  Chip,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CircularProgress from "@mui/material/CircularProgress";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ErrorIcon from "@mui/icons-material/Error";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useTheme } from "@mui/material/styles";
import {
  StageResponse,
  getReportTypeDisplayName,
  stageDisplayConfig,
} from "../services/ProposalGenerationServices";
import { useGetStageConfig } from "../services/ProposalGenerationConfigService";
import { Can } from "src/helpers/Auth";

interface StagesPanelProps {
  stages: StageResponse[];
  currentDialogueId: number;
  onStageClick: (dialogueId: number, stageNumber?: number) => void;
  onClearStage?: (dialogueId: number) => void;
  onRemoveStage?: (dialogueId: number) => void;
  onAddStage?: (reportType: string) => void;
  isMutating?: boolean;
}

export const StagesPanel: React.FC<StagesPanelProps> = ({
  stages,
  currentDialogueId,
  onStageClick,
  onClearStage,
  onRemoveStage,
  onAddStage,
  isMutating = false,
}) => {
  const theme = useTheme();
  const { data: stageConfig } = useGetStageConfig();

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    type: "clear" | "remove";
    dialogueId: number;
    stageTitle: string;
  }>({ open: false, type: "clear", dialogueId: 0, stageTitle: "" });

  // Add stage dialog state
  const [addDialog, setAddDialog] = useState<{
    open: boolean;
    reportType: string;
  }>({ open: false, reportType: "" });

  const getStageIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircleIcon sx={{ color: theme.palette.success.main }} />;
      case "in_progress":
        return <CircularProgress size={24} color="primary" />;
      case "failed":
        return <ErrorIcon color="error" />;
      default:
        return (
          <RadioButtonUncheckedIcon sx={{ color: theme.palette.grey[400] }} />
        );
    }
  };

  const formatStageTitle = (stage: StageResponse) => {
    return getReportTypeDisplayName(
      stage.report_type,
      stage.sequence_number,
      stageDisplayConfig(
        stage.stage_report_prefix,
        stage.stage_report_suffix_label,
        stageConfig?.effective,
      ),
    );
  };

  const handleStageAction = (stage: StageResponse) => {
    console.log("[StagesPanel] Stage clicked:", {
      dialogueId: stage.dialogue_id,
      sequenceNumber: stage.sequence_number,
      status: stage.status,
    });

    onStageClick(stage.dialogue_id, stage.sequence_number);
  };

  const handleClearClick = (e: React.MouseEvent, stage: StageResponse) => {
    e.stopPropagation();
    setConfirmDialog({
      open: true,
      type: "clear",
      dialogueId: stage.dialogue_id,
      stageTitle: formatStageTitle(stage),
    });
  };

  const handleRemoveClick = (e: React.MouseEvent, stage: StageResponse) => {
    e.stopPropagation();
    setConfirmDialog({
      open: true,
      type: "remove",
      dialogueId: stage.dialogue_id,
      stageTitle: formatStageTitle(stage),
    });
  };

  const handleConfirmAction = () => {
    if (confirmDialog.type === "clear" && onClearStage) {
      onClearStage(confirmDialog.dialogueId);
    } else if (confirmDialog.type === "remove" && onRemoveStage) {
      onRemoveStage(confirmDialog.dialogueId);
    }
    setConfirmDialog({ ...confirmDialog, open: false });
  };

  const handleAddStage = () => {
    if (onAddStage && addDialog.reportType.trim()) {
      onAddStage(addDialog.reportType.trim());
      setAddDialog({ open: false, reportType: "" });
    }
  };

  if (stages.length === 0) {
    return (
      <Box data-testid="propgen-stages-list" sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No stages available yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box data-testid="propgen-stages-list" sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Workflow Stages
      </Typography>
      <List>
        {stages.map((stage) => (
          <ListItem key={stage.dialogue_id} disablePadding>
            <ListItemButton
              selected={stage.dialogue_id === currentDialogueId}
              onClick={() => handleStageAction(stage)}
              data-testid={`propgen-stage-${stage.sequence_number}`}
              sx={{
                flexDirection: "column",
                alignItems: "flex-start",
                py: 1.5,
                px: 2,
              }}
            >
              {/* Top row: icon + stage name */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  gap: 1,
                }}
              >
                <Box sx={{ flexShrink: 0, display: "flex" }}>
                  {getStageIcon(stage.status)}
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    wordBreak: "break-word",
                  }}
                >
                  {formatStageTitle(stage)}
                </Typography>
              </Box>
              {/* Bottom row: stage number, status chip, actions */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  mt: 0.5,
                  pl: 4,
                  gap: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mr: "auto" }}
                >
                  Stage {stage.sequence_number}
                </Typography>
                {stage.status !== "pending" && (
                  <Chip
                    label={stage.status.replace("_", " ")}
                    size="small"
                    data-testid={`propgen-stage-status-${stage.sequence_number}`}
                    color={
                      stage.status === "completed"
                        ? "success"
                        : stage.status === "in_progress"
                          ? "primary"
                          : stage.status === "failed"
                            ? "error"
                            : "default"
                    }
                  />
                )}
                {stage.status !== "pending" && onClearStage && (
                  <Can
                    perform="artifact:write"
                    yes={() => (
                      <Tooltip title="Reset stage">
                        <span>
                          <IconButton
                            size="small"
                            aria-label={`Reset ${formatStageTitle(stage)}`}
                            onClick={(e) => handleClearClick(e, stage)}
                            disabled={isMutating}
                          >
                            <RestartAltIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    no={() => (
                      <Tooltip title="User doesn't have permissions artifact:write">
                        <span>
                          <IconButton
                            size="small"
                            aria-label={`Reset ${formatStageTitle(stage)}`}
                            disabled
                          >
                            <RestartAltIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  />
                )}
                {stage.sequence_number !== 1 && onRemoveStage && (
                  <Can
                    perform="artifact:write"
                    yes={() => (
                      <Tooltip title="Remove stage">
                        <span>
                          <IconButton
                            size="small"
                            aria-label={`Remove ${formatStageTitle(stage)}`}
                            onClick={(e) => handleRemoveClick(e, stage)}
                            disabled={isMutating}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    no={() => (
                      <Tooltip title="User doesn't have permissions artifact:write">
                        <span>
                          <IconButton
                            size="small"
                            aria-label={`Remove ${formatStageTitle(stage)}`}
                            disabled
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  />
                )}
              </Box>
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {onAddStage && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
          <Can
            perform="artifact:write"
            yes={() => (
              <Tooltip title="Add stage">
                <span>
                  <IconButton
                    aria-label="Add stage"
                    onClick={() => setAddDialog({ open: true, reportType: "" })}
                    color="primary"
                    disabled={isMutating}
                  >
                    <AddCircleOutlineIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            no={() => (
              <Tooltip title="User doesn't have permissions artifact:write">
                <span>
                  <IconButton aria-label="Add stage" color="primary" disabled>
                    <AddCircleOutlineIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          />
        </Box>
      )}

      {/* Confirmation Dialog for Clear / Remove */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
      >
        <DialogTitle>
          {confirmDialog.type === "clear" ? "Reset Stage?" : "Remove Stage?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.type === "clear"
              ? `Reset "${confirmDialog.stageTitle}"? All progress will be lost.`
              : `Remove "${confirmDialog.stageTitle}"? This cannot be undone.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            color={confirmDialog.type === "remove" ? "error" : "primary"}
            variant="contained"
          >
            {confirmDialog.type === "clear" ? "Reset" : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Stage Dialog */}
      <Dialog
        open={addDialog.open}
        onClose={() => setAddDialog({ open: false, reportType: "" })}
      >
        <DialogTitle>Add Stage</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the report type for the new stage.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Report Type"
            placeholder={`e.g. ${stageConfig?.effective.report_type_prefix || "discipline_report_"}structural_engineering`}
            value={addDialog.reportType}
            onChange={(e) =>
              setAddDialog({ ...addDialog, reportType: e.target.value })
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" && addDialog.reportType.trim()) {
                handleAddStage();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialog({ open: false, reportType: "" })}>
            Cancel
          </Button>
          <Button
            onClick={handleAddStage}
            color="primary"
            variant="contained"
            disabled={!addDialog.reportType.trim()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
